import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import "bootstrap/dist/css/bootstrap.min.css";
import DateRangePickerComponent from './CustomDateRangePicker';
import Swal from "sweetalert2";

const SewadarLoginLogs = () => {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [sewadars, setSewadars] = useState([]);
  const [selectedSewadars, setSelectedSewadars] = useState([]);

  const [logs, setLogs] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [expandedSewadars, setExpandedSewadars] = useState({}); // track expanded state

  const token = localStorage.getItem("token");
  const code = localStorage.getItem("code");

  const fetchDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const response = await apiClient.get(SERVER_BASE + "/departments", {
        headers: { Authorization: `Bearer ${token}` },
        params: { code },
      });
      const options = response.data.map((d) => ({
        value: d.name,
        label: d.departmentName || d.name,
      }));
      setDepartments(options);
    } catch (error) {
      console.error("Error fetching departments:", error);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const fetchSewadars = async () => {
    try {
      setLoadingData(true);
      const response = await apiClient.get(SERVER_BASE + "/getSewadar", {
        params: { code },
        headers: { Authorization: `Bearer ${token}` },
      });
      const options = response.data.map((s) => ({
        value: s.id,
        label: `${s.firstName} ${s.lastName}`,
        sewadarOf: s.sewadarOf,
      }));
      setSewadars(options);
    } catch (error) {
      console.error("Error fetching Sewadars:", error);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchSewadars();
  }, []);

  const filteredSewadars = useMemo(() => {
    if (!selectedDepartment) return sewadars;
    return sewadars.filter(
      (s) =>
        s.sewadarOf === selectedDepartment.label ||
        s.sewadarOf === selectedDepartment.value
    );
  }, [selectedDepartment, sewadars]);

  const fetchLoginLogs = async () => {
    const [startDate, endDate] = dateRange;

    if (!selectedDepartment && selectedSewadars.length === 0 && !startDate && !endDate) {
      Swal.fire({
        icon: "warning",
        title: "Select at least one filter",
        text: "Please select atleast one option",
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000
      });
      return;
    }

    try {
      setLoadingData(true);
      const payload = {
        department: selectedDepartment?.value || null,
        sewadarIds: selectedSewadars.map((s) => s.value),
        startDate: startDate || null,
        endDate: endDate || null,
      };

      const response = await apiClient.post(
        `${SERVER_BASE}/getLoginLogsFiltered`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLogs(response.data.logs || []);
      setExpandedSewadars({}); // reset expanded state
    } catch (error) {
      console.error("Error fetching logs:", error);
      setLogs([]);
    } finally {
      setLoadingData(false);
    }
  };

  const groupedLogs = useMemo(() => {
    const map = {};
    logs.forEach((log) => {
      if (!map[log.sewadarName]) map[log.sewadarName] = [];
      map[log.sewadarName].push(log);
    });
    return map;
  }, [logs]);

  const toggleExpand = (name) => {
    setExpandedSewadars(prev => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <div className="formcontainer mt-5">
      <h4 className="mb-4">Sewadar Login Logs</h4>

      <div className="row align-items-end g-3 mb-3">
        <div className="col-md-3">
          <label className="form-label fw-bold">Department</label>
          <Select
            options={departments}
            value={selectedDepartment}
            onChange={setSelectedDepartment}
            isClearable
            placeholder="Choose Department..."
            isLoading={loadingDepartments}
          />
        </div>

        <div className="col-md-5">
          <label className="form-label fw-bold">Sewadars</label>
          <Select
            options={filteredSewadars}
            value={selectedSewadars}
            onChange={setSelectedSewadars}
            isMulti
            isClearable
            placeholder="Choose Sewadar(s)..."
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Date Range</label>
          <DateRangePickerComponent value={dateRange} onChange={setDateRange} />
        </div>

        <div className="col-md-1 d-grid">
          <button
            className="btn btn-primary fw-bold"
            onClick={fetchLoginLogs}
            disabled={loadingData}
          >
            {loadingData ? "Loading..." : "Get"}
          </button>
        </div>
      </div>

      {!loadingData && logs.length > 0 && (
        <div className="table-responsive mt-4">
          <table className="table table-bordered table-striped align-middle">
            <thead className="table-secondary">
              <tr>
                <th>Sewadar Name</th>
                <th>Login Time</th>
                <th>Status</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedLogs).map(([name, logs]) => (
                <>
                  {logs.map((log, i) => {
                    // Show only first row if not expanded
                    if (i > 0 && !expandedSewadars[name]) return null;

                    return (
                      <tr key={`${name}-${i}`}>
                        {i === 0 && (
                          <td
                            rowSpan={expandedSewadars[name] ? logs.length : 1}
                            className="fw-bold align-middle"
                          >
                            {name} - 
                            <small className="text-muted"> {log.sewadarOf}</small>
                            {logs.length > 1 && (
                              <button
                                className="btn btn-sm ms-1"
                                onClick={() => toggleExpand(name)}
                              >
                                {expandedSewadars[name] ? '▼ ' : '▶ '}
                              </button>
                            )}
                          </td>
                        )}
                        <td>
                          {new Date(log.loginTime).toLocaleString("en-IN", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </td>
                        <td
                          style={{
                            color: log.status?.toLowerCase() === "success" ? "green" : "red",
                            fontWeight: 600,
                          }}
                        >
                          {log.status}
                        </td>
                        <td>{log.message}</td>
                      </tr>
                    );
                  })}
                </>
              ))}
            </tbody>

          </table>
        </div>
      )}

      {!loadingData && logs.length === 0 && (
        <div className="alert alert-secondary text-center mt-3">
          No login logs found for selected filters.
        </div>
      )}
    </div>
  );
};

export default SewadarLoginLogs;
